// ==========================
// Version 1 — pages/AdminMemoryDrawer.js
// - Slide-over drawer for reviewing a single memory
// - Loads full memory via getMemoryById()
// - Shows meta, story, location, thumbnails
// - Inline actions: Hide / Unhide / Delete (with optimistic callbacks)
// ==========================
import React from 'react';
import { getMemoryById, adminHideMemory, adminUnhideMemory, adminDeleteMemory } from '../db';
import { useAuth } from '../context/AuthContext';

const NAVY = '#0A1A33';
const IVORY = '#FDFCF9';
const GOLD = '#C9A44C';
const WARM_GRAY = '#888888';
const ACCESSIBLE_GOLD = '#8A6D2E';
const BURGUNDY = '#5C1A2B';
const ROSE_CLAY = '#B76E79';
const TEAL = '#0F766E';

export default function AdminMemoryDrawer({
  open,
  memoryId,
  initialRow = null,     // optional { id, title, userId, isPublic, isMediaPublic, createdAt }
  onClose,
  onUpdated,             // (partial) => void  e.g. {isPublic:false,isMediaPublic:false}
  onDeleted,             // () => void
}) {
  const { currentUser } = useAuth();
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [mem, setMem] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  // Close on ESC
  React.useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose?.(); }
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Load full memory when opened
  React.useEffect(() => {
    let alive = true;
    if (!open || !memoryId) { setMem(null); setErr(''); return; }
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const full = await getMemoryById(memoryId);
        if (!alive) return;
        if (full) setMem(full);
        else setErr('Memory not found.');
      } catch (e) {
        if (!alive) return;
        // eslint-disable-next-line no-console
        console.error('[AdminMemoryDrawer] getMemoryById failed:', e);
        setErr('Failed to load memory.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, memoryId]);

  const row = React.useMemo(() => {
    // Merge a minimal row (from table) with loaded mem
    const base = initialRow || {};
    const m = mem || {};
    return {
      id: memoryId,
      title: m.title ?? base.title ?? 'Untitled',
      date: m.date ?? base.date ?? '',
      story: m.story ?? '',
      userId: m.userId ?? base.userId ?? '',
      isPublic: !!(m.isPublic ?? base.isPublic),
      isMediaPublic: !!(m.isMediaPublic ?? base.isMediaPublic),
      createdAt: m.createdAt?.toDate ? m.createdAt.toDate() : base.createdAt || null,
      imageUrls: Array.isArray(m.imageUrls) ? m.imageUrls : [],
      location:
        typeof m.location === 'object' && m.location
          ? m.location
          : { lat: m.lat ?? null, lng: m.lng ?? null },
      region: m.region ?? m.administrative_area ?? null,
      city: m.city ?? m.locality ?? m.placeName ?? null,
      formattedAddress: m.formattedAddress ?? null,
      country: m.country ?? null,
    };
  }, [mem, initialRow, memoryId]);

  async function doHide() {
    if (!currentUser?.uid || !row.id) return;
    setBusy(true); setErr('');
    try {
      await adminHideMemory({ memoryId: row.id, actorUid: currentUser.uid, ownerUid: row.userId });
      onUpdated?.({ isPublic: false, isMediaPublic: false });
      setMem((m) => ({ ...(m || {}), isPublic: false, isMediaPublic: false }));
    } catch (e) {
      setErr(e?.message || 'Failed to hide memory.');
    } finally {
      setBusy(false);
    }
  }

  async function doUnhide() {
    if (!currentUser?.uid || !row.id) return;
    setBusy(true); setErr('');
    try {
      await adminUnhideMemory({ memoryId: row.id, actorUid: currentUser.uid, ownerUid: row.userId });
      onUpdated?.({ isPublic: true, isMediaPublic: true });
      setMem((m) => ({ ...(m || {}), isPublic: true, isMediaPublic: true }));
    } catch (e) {
      setErr(e?.message || 'Failed to unhide memory.');
    } finally {
      setBusy(false);
    }
  }

  async function doDelete() {
    if (!currentUser?.uid || !row.id) return;
    if (!window.confirm('Delete this memory and its media? This cannot be undone.')) return;
    setBusy(true); setErr('');
    try {
      await adminDeleteMemory({ memoryId: row.id, actorUid: currentUser.uid, ownerUid: row.userId });
      onDeleted?.();
      onClose?.();
    } catch (e) {
      setErr(e?.message || 'Failed to delete memory.');
    } finally {
      setBusy(false);
    }
  }

  // Drawer hidden state
  return (
    <div
      style={{
        pointerEvents: open ? 'auto' : 'none',
        opacity: open ? 1 : 0,
        transition: 'opacity 160ms ease',
      }}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.45)',
          zIndex: 9998,
          display: open ? 'block' : 'none',
        }}
      />

      {/* Panel */}
      <aside
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100dvh',
          width: 'min(520px, 92vw)',
          background: IVORY,
          color: NAVY,
          borderLeft: '1px solid rgba(201,164,76,0.35)',
          boxShadow: '-8px 0 28px rgba(10,26,51,0.35)',
          zIndex: 9999,
          transform: open ? 'translateX(0)' : 'translateX(105%)',
          transition: 'transform 200ms ease',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 12, borderBottom: '1px solid rgba(10,26,51,0.1)' }}>
          <div style={{ fontFamily: 'serif', fontSize: 18, fontWeight: 800 }}>Memory</div>
          <button type="button" onClick={onClose} style={btnTiny}>Close</button>
        </div>

        {/* Body */}
        <div style={{ padding: 12, overflow: 'auto' }}>
          {err ? <div style={errBox}>{err}</div> : null}
          {loading ? (
            <div style={{ color: WARM_GRAY }}>Loading…</div>
          ) : !row.id ? (
            <div style={{ color: WARM_GRAY }}>No memory selected.</div>
          ) : (
            <>
              <div style={{ display: 'grid', gap: 6 }}>
                <div style={{ fontWeight: 800, fontSize: 18, fontFamily: 'serif' }}>{row.title}</div>
                <div style={{ color: WARM_GRAY, fontSize: 13 }}>
                  {row.createdAt ? row.createdAt.toLocaleString() : '—'}
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 }}>
                  <Badge label={row.isPublic ? 'Public' : 'Private'} ok={row.isPublic} />
                  <Badge label={row.isMediaPublic ? 'Media Public' : 'Media Private'} ok={row.isMediaPublic} />
                  <code style={{ fontSize: 12 }}>{row.userId || '—'}</code>
                </div>

                {/* Location compact */}
                <LocLine row={row} />

                {/* Story */}
                {row.story ? (
                  <div style={block}>
                    <div style={blockTitle}>Story</div>
                    <p style={{ margin: 0, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{row.story}</p>
                  </div>
                ) : null}

                {/* Media */}
                {Array.isArray(row.imageUrls) && row.imageUrls.length > 0 ? (
                  <div style={block}>
                    <div style={blockTitle}>Media</div>
                    <div style={thumbGrid}>
                      {row.imageUrls.map((u, i) => (
                        <div key={i} style={thumbWrap}>
                          <img src={u} alt="" style={thumbImg} />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  {row.isPublic ? (
                    <button type="button" style={btnMinor} disabled={busy} onClick={doHide} title="Set isPublic=false & isMediaPublic=false">
                      Hide
                    </button>
                  ) : (
                    <button type="button" style={btnMinor} disabled={busy} onClick={doUnhide} title="Set isPublic=true & isMediaPublic=true">
                      Unhide
                    </button>
                  )}
                  <a href={`/m/${row.id}`} target="_blank" rel="noopener noreferrer" style={btnLink} title="Open public page">
                    Open ↗
                  </a>
                  <button type="button" style={{ ...btnMinor, borderColor: ROSE_CLAY, color: BURGUNDY }} disabled={busy} onClick={doDelete}>
                    Delete
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

function Badge({ label, ok }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 999,
      border: `1px solid ${ok ? 'rgba(15,118,110,0.42)' : GOLD}`,
      background: ok ? 'rgba(15,118,110,0.10)' : 'rgba(201,164,76,0.12)',
      color: ok ? TEAL : ACCESSIBLE_GOLD,
      fontWeight: 800,
      fontSize: 12
    }}>
      {label}
    </span>
  );
}

function LocLine({ row }) {
  const label = (() => {
    const parts = [];
    if (row.region) parts.push(row.region);
    if (row.city) parts.push(row.city);
    const human = parts.join(', ');
    if (human) return human;
    const lat = row.location?.lat;
    const lng = row.location?.lng;
    if (typeof lat === 'number' && typeof lng === 'number') return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    return row.formattedAddress || row.country || '';
  })();
  if (!label) return null;
  return (
    <div style={{ color: '#6f7a8c', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
      <span aria-hidden>📍</span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
    </div>
  );
}

/* styles */
const btnTiny = {
  appearance: 'none',
  background: 'transparent',
  color: NAVY,
  border: `1px solid ${GOLD}`,
  padding: '4px 8px',
  borderRadius: 8,
  cursor: 'pointer',
  fontWeight: 700,
  fontSize: 12,
};
const btnMinor = {
  appearance: 'none',
  background: 'transparent',
  color: NAVY,
  border: `1px solid ${GOLD}`,
  padding: '6px 10px',
  borderRadius: 10,
  cursor: 'pointer',
  fontWeight: 700,
};
const btnLink = {
  textDecoration: 'none',
  color: NAVY,
  border: `1px solid ${GOLD}`,
  padding: '6px 10px',
  borderRadius: 10,
  fontWeight: 800,
  background: '#fff',
};
const errBox = {
  color: BURGUNDY,
  background: 'rgba(92,26,43,0.08)',
  border: '1px dashed rgba(183,110,121,0.55)',
  padding: 10,
  borderRadius: 10,
  margin: '8px 0',
};
const block = { marginTop: 8 };
const blockTitle = { fontWeight: 800, marginBottom: 6, color: NAVY };
const thumbGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 8 };
const thumbWrap = { border: '1px solid rgba(201,164,76,0.25)', borderRadius: 10, overflow: 'hidden', background: '#fff' };
const thumbImg = { width: '100%', height: 96, objectFit: 'cover', display: 'block' };
// ==========================
// End Version 1 — pages/AdminMemoryDrawer.js
// ==========================
